/**
 * PROD-3115: a clinic panel can send some of its tests to another lab. Each
 * test row carries its own requisition (Scrips.Patient PROD-2841), and the
 * encounter's requisitions read lists each test under its lab. These helpers
 * turn an order's tests at another lab into a "split part" row for that lab's
 * block on the lab desk. Its status comes from its test rows.
 */
import {
  LAB_ORDER_STATUS,
  type Coding,
  type EncounterLabRequisitions,
  type LabRequisition,
  type LabRequisitionTest,
  type PatientLabOrderDetail,
} from '@/types/scheduling-api';

/**
 * PROD-3115: the part of a lab order that goes to another lab. Its status
 * lives on its test rows (Scrips.Patient PROD-3115), not on the order.
 */
export interface LabSplitPart {
  labRequestId: string;
  requisitionId: string;
  /** The ordered panel's name, for the "Part of ..." marker. */
  orderName: string | null;
}

export function partOfLabel(orderName: string | null | undefined): string {
  return `Part of ${orderName?.trim() || 'a clinic panel'}`;
}

/** Test row statuses that take a test out of the part (it will not be done). */
const NOT_DONE = new Set(['Cancelled', 'NotPerformed']);

/**
 * PROD-3115: the lab desk status of a split part, from its test rows
 * (`PatientLabRequestTest.Status`). Cancelled / not performed tests are left
 * out; when nothing is left the part is Cancelled. Then: every test Resulted
 * -> Result received; any test collected or at the lab and none still
 * Ordered -> Sample collected; anything else -> Requested.
 */
export function splitPartStatus(tests: LabRequisitionTest[]): string {
  const live = tests.map((t) => t.status ?? 'Ordered').filter((s) => !NOT_DONE.has(s));
  if (live.length === 0) return LAB_ORDER_STATUS.Cancelled;
  if (live.every((s) => s === 'Resulted')) return LAB_ORDER_STATUS.ResultReceived;
  if (!live.includes('Ordered') && live.some((s) => s === 'Collected' || s === 'InLab')) {
    return LAB_ORDER_STATUS.SampleCollected;
  }
  return LAB_ORDER_STATUS.Requested;
}

export function toCoding(t: LabRequisitionTest): Coding {
  return { code: t.code ?? '', display: t.display ?? t.code ?? '', system: t.system ?? null };
}

/** The order's tests, by the requisition each one is in. */
export function testsByRequisition(
  row: PatientLabOrderDetail,
  read: EncounterLabRequisitions | undefined,
): Map<string, { requisition: LabRequisition; tests: LabRequisitionTest[] }> {
  const out = new Map<string, { requisition: LabRequisition; tests: LabRequisitionTest[] }>();
  if (!row.id || !read || read.testsAvailable === false) return out;
  for (const q of read.requisitions ?? []) {
    const tests = (q.tests ?? []).filter((t) => t.labRequestId === row.id);
    if (tests.length > 0) out.set(q.id, { requisition: q, tests });
  }
  return out;
}

/**
 * PROD-3157: the order row in its own lab's block, with only that lab's
 * tests. A clinic panel split across labs claims no lab vendor
 * (Scrips.Patient), so the row takes its lab from its own requisition.
 */
export function ownPartRow(
  row: PatientLabOrderDetail,
  requisition: LabRequisition,
  tests: LabRequisitionTest[],
): PatientLabOrderDetail {
  return {
    ...row,
    labOrderValueSetTestDetails: tests.map(toCoding),
    labVendorId: requisition.labVendorId ?? row.labVendorId ?? null,
    labVendorName: requisition.labVendorName ?? row.labVendorName ?? null,
  };
}

export function splitPartRow(
  row: PatientLabOrderDetail,
  requisition: LabRequisition,
  tests: LabRequisitionTest[],
): PatientLabOrderDetail & { splitPart: LabSplitPart } {
  const single = tests.length === 1 ? tests[0] : null;
  return {
    ...row,
    orderName: single ? (single.display ?? single.code ?? row.orderName) : row.orderName,
    cptCode: single ? (single.code ?? null) : row.cptCode,
    orderDescription: null,
    labOrderValueSetTestDetails: single ? [] : tests.map(toCoding),
    labVendorId: requisition.labVendorId ?? null,
    labVendorName: requisition.labVendorName ?? null,
    fulfilmentMode: requisition.fulfilmentMode ?? null,
    requisitionId: requisition.id,
    orderStatus: splitPartStatus(tests),
    documentUrl: null,
    patientDocumentId: null,
    splitPart: {
      labRequestId: row.id ?? '',
      requisitionId: requisition.id,
      orderName: row.orderName ?? null,
    },
  };
}

const SPLIT_PART_CONFLICT_ERROR =
  'This order was changed by someone else. Reload it and try again.';
const SPLIT_PART_GENERAL_ERROR = 'Could not update this order. Try again.';

/**
 * PROD-3115: the message for a failed split part status change. A 409 from
 * Scrips.Patient carries `{ error }` (the part changed meanwhile, or it
 * already has a result): show that text, else the "changed" one. Any other
 * failure (500, network) gets a general message.
 */
export function splitPartErrorMessage(err: unknown): string {
  if ((err as { status?: unknown } | null)?.status !== 409) return SPLIT_PART_GENERAL_ERROR;
  const body = (err as { body?: unknown } | null)?.body;
  if (typeof body === 'string' && body.trim()) {
    try {
      const parsed = JSON.parse(body) as { error?: unknown };
      if (typeof parsed.error === 'string' && parsed.error.trim()) return parsed.error;
    } catch {
      // not JSON: use the general message
    }
  }
  return SPLIT_PART_CONFLICT_ERROR;
}
